import { useState } from 'react';
import { getAuthToken } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '@/config/constants';
import { SparklesIcon } from '@heroicons/react/24/outline';

interface AIKeywordSuggestButtonProps {
  title?: string;
  content?: string;
  category?: string;
  onKeywordsGenerated: (keywords: string[]) => void;
}

const AIKeywordSuggestButton = ({
  title,
  content,
  category,
  onKeywordsGenerated,
}: AIKeywordSuggestButtonProps) => {
  const [loading, setLoading] = useState(false);

  const generateKeywords = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();

      const prompt = `Generate 8-12 SEO-optimized meta keywords for a blog post titled: '${title || ''}'. Category: ${category || 'general'}. Return ONLY a comma-separated list of keywords, nothing else. Do not number them.`;

      const response = await fetch(`${API_ENDPOINTS.ai}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt,
          type: 'custom',
          context: { title, category, content: content?.substring(0, 500) },
        }),
      });

      const result = await response.json();

      if (result.success) {
        const raw = result.data?.content || result.data || '';
        const keywords = raw
          .split(',')
          .map((k: string) => k.trim())
          .filter((k: string) => k.length > 0);
        onKeywordsGenerated(keywords);
      }
    } catch (error) {
      console.error('AI keyword generation error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={generateKeywords}
      disabled={loading}
      className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium text-purple-600 hover:text-purple-700 disabled:opacity-50 transition-colors"
    >
      {loading ? (
        <div className="animate-spin rounded-full h-3 w-3 border border-purple-600 border-t-transparent" />
      ) : (
        <SparklesIcon className="h-3 w-3" />
      )}
      <span>{loading ? 'Generating...' : 'AI Keywords'}</span>
    </button>
  );
};

export default AIKeywordSuggestButton;
